import { memo } from "react";
import { formatCurrency, transformDateToLocalDateString } from "helpers";
import Tooltip from "ui/components/Tooltip/Tooltip";
import { Badge } from "ui/components/Badge";
import { InvoiceIncoming } from "services/api/finance/incoming-invoices/types";

export interface InvoiceIncomingCellProps {
  invoice: InvoiceIncoming | null | undefined;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

const InvoiceIncomingCell: React.FC<InvoiceIncomingCellProps> = ({ invoice, onClick }) => {
  if (!invoice || !invoice.invoiceNumber) return <></>;

  return (
    <Tooltip
      decorate={
        <div
          className="text-sm font-medium text-sky-600 hover:text-sky-900 cursor-pointer truncate"
          onClick={onClick}
        >
          {invoice.invoiceNumber}
        </div>
      }
      position="bottom-left"
      size="medium"
    >
        <div className="min-w-[260px]">
          <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">Ulazni račun</div>

          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-300">Broj računa</span>
            <span className="font-medium">{invoice.invoiceNumber}</span>
          </div>

          {invoice.status && (
            <div className="flex justify-between text-sm mb-1 items-center">
              <span className="text-slate-300">Status</span>
              <Badge text={invoice.status.name} variant={invoice.isPaid ? "success-dark" : "info"} height="small" showDot />
            </div>
          )}

          {(invoice.contactSupplier?.name || invoice.supplierName) && (
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">Dobavljač</span>
              <span className="font-medium text-right max-w-[150px] truncate">{invoice.contactSupplier?.name ?? invoice.supplierName}</span>
            </div>
          )}

          {invoice.supplierOIB && (
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">OIB</span>
              <span className="font-medium">{invoice.supplierOIB}</span>
            </div>
          )}

          {invoice.invoiceDate && (
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">Datum računa</span>
              <span className="font-medium">{transformDateToLocalDateString(invoice.invoiceDate)}</span>
            </div>
          )}

          {invoice.dueDate && (
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">Dospijeće</span>
              <span className="font-medium">{transformDateToLocalDateString(invoice.dueDate)}</span>
            </div>
          )}

          {invoice.total != null && (
            <>
              <div className="border-t border-slate-600 my-2"></div>
              {invoice.totalWithoutTax != null && (
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-300">Bez PDV-a</span>
                  <span className="font-medium">{formatCurrency.format(invoice.totalWithoutTax)}</span>
                </div>
              )}
              {invoice.totalTax != null && (
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-300">PDV</span>
                  <span className="font-medium">{formatCurrency.format(invoice.totalTax)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-slate-300">Ukupno</span>
                <span className="font-bold text-green-400">{formatCurrency.format(invoice.total)}</span>
              </div>
            </>
          )}
        </div>
      </Tooltip>
  );
};

export default memo(InvoiceIncomingCell);
